import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "../services/api";
import ConfirmModal from "../components/ConfirmModal";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import { useTheme } from "../hooks/useTheme";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import type { Kid, FamilyMember, FamilyInvite } from "@starquest/core";
import styles from "./ParentHome.module.css";
import own from "./Explorers.module.css";

const AVATARS = ["🧑‍🚀","🦸","🧙","🦄","🐉","🦊","🐼","🦁","🐯","🦋"];

function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate())) age--;
  return age;
}
const PARENT_AVATARS = ["👨","👩","🧑","👨‍💼","👩‍💼","🧔","👱","🧑‍🦱","🧑‍🦰","🧑‍🦳","🧑‍🦲","🥷"];

type EditState = { kidId: string; name: string; avatar: string; dob: string } | null;
type MemberEditState = { memberId: string; displayName: string; avatar: string; role: string; canChangeRole: boolean; isSelf: boolean; currentEmail: string; newEmailDirect: string } | null;

export default function FamilyHub() {
  const { familyId, isAdmin, isSuper, isViewer, canManageKids, email } = useAuthStore();
  const { kids, completions, members, setKids, setCompletions, setMembers } = useFamilyStore();
  const theme = useTheme(); // base theme

  // Co-parent state

  const emailLower = email?.toLowerCase() ?? "";
  const displayMembers: FamilyMember[] = members.some((m) => m.email?.toLowerCase() === emailLower)
    ? members
    : emailLower
      ? [{ memberId: "current-user", familyId: familyId ?? "", email: emailLower, role: "admin" as const, addedAt: "" }, ...members]
      : members;
  const [invites,    setInvites]    = useState<FamilyInvite[]>([]);
  const [inviteUrl,  setInviteUrl]  = useState<string | null>(null);
  const [inviting,   setInviting]   = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [copiedInvite, setCopiedInvite] = useState<Record<string, boolean>>({});
  const [inviteRole,    setInviteRole]   = useState<"manager" | "viewer">("manager");
  const [pickingRole,   setPickingRole]  = useState(false);

  // Three-dot menu
  const [openMenu, setOpenMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [confirmRemoveId,    setConfirmRemoveId]    = useState<string | null>(null);
  const [confirmRevokeCode,  setConfirmRevokeCode]  = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!openMenu) return;
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenu]);

  function openCardMenu(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setOpenMenu({ id, x: r.right, y: r.bottom + 4 });
  }

  // Member profile edit state
  const [editingMember, setEditingMember] = useState<MemberEditState>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  // Email change sub-flow
  const [emailStep,  setEmailStep]  = useState<"idle" | "enter" | "verify">("idle");
  const [newEmail,   setNewEmail]   = useState("");
  const [emailCode,  setEmailCode]  = useState("");
  const [emailError, setEmailError] = useState("");

  // Kid management state
  const [adding,    setAdding]   = useState(false);
  const [newName,   setNewName]  = useState("");
  const [newAva,    setNewAva]   = useState("🧑‍🚀");
  const [newPin,    setNewPin]   = useState("");
  const [newDob,    setNewDob]   = useState("");
  const [editing,   setEditing]  = useState<EditState>(null);
  const [loading,   setLoading]  = useState(false);
  const [confirm,   setConfirm]  = useState<string | null>(null);
  const [resetPin,  setResetPin] = useState<{ kidId: string; pin: string } | null>(null);
  const [familyCode,    setFamilyCode]    = useState<string | null>(null);
  const [copiedCode,    setCopiedCode]    = useState(false);
  const [editingCode,   setEditingCode]   = useState(false);
  const [codeDraft,     setCodeDraft]     = useState("");
  const [codeError,     setCodeError]     = useState("");
  const [savingCode,    setSavingCode]    = useState(false);

  useEffect(() => {
    if (!familyId) return;
    Promise.all([
      api.listKids(familyId).then((k) => setKids(k as Kid[])),
      api.listCompletions(familyId, "pending").then((c) => setCompletions(c as any)),
      api.listMembers(familyId).then((m) => setMembers(m as FamilyMember[])),
      ...(isSuper ? [api.listInvites(familyId).then((i) => setInvites(i as FamilyInvite[]))] : []),
      api.getFamilyCode(familyId).then((r) => setFamilyCode(r.familyCode)).catch(() => {}),
    ]);
  }, [familyId]);

  async function generateInvite() {
    if (!familyId) return;
    setInviting(true);
    try {
      const res = await api.createInvite(familyId, inviteRole);
      setInviteUrl(res.inviteUrl);
      api.listInvites(familyId).then((i) => setInvites(i as FamilyInvite[]));
    } catch { alert("Failed to generate invite"); }
    finally { setInviting(false); }
  }

  async function copyInvite() {
    if (!inviteUrl) return;
    try { await navigator.clipboard.writeText(inviteUrl); } catch { /* fallback */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function copyInviteLink(inviteCode: string) {
    const url = `${window.location.origin}/join/${inviteCode}`;
    try { await navigator.clipboard.writeText(url); } catch { /* fallback */ }
    setCopiedInvite((prev) => ({ ...prev, [inviteCode]: true }));
    setTimeout(() => setCopiedInvite((prev) => ({ ...prev, [inviteCode]: false })), 2000);
  }

  async function shareInviteLink(inviteCode: string) {
    const url = `${window.location.origin}/join/${inviteCode}`;
    if (navigator.share) {
      await navigator.share({ title: `Join ${theme.familyHubTitle} on StarQuest`, url });
    } else {
      await copyInviteLink(inviteCode);
    }
  }

  async function shareInvite() {
    if (!inviteUrl) return;
    if (navigator.share) {
      await navigator.share({ title: "Join Family Hub on StarQuest", url: inviteUrl });
    } else {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function saveMemberEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingMember || !familyId) return;
    setMemberLoading(true);
    try {
      const updates: Record<string, unknown> = {
        displayName: editingMember.displayName.trim() || undefined,
        avatar: editingMember.avatar,
        ...(editingMember.canChangeRole ? { role: editingMember.role } : {}),
      };
      await api.updateMember(familyId, editingMember.memberId, updates);
      // Admin direct email change (no OTP)
      if (!editingMember.isSelf && editingMember.newEmailDirect.trim() && editingMember.newEmailDirect.trim() !== editingMember.currentEmail) {
        await api.updateMemberEmail(familyId, editingMember.memberId, editingMember.newEmailDirect.trim());
      }
      api.listMembers(familyId).then((m) => setMembers(m as FamilyMember[]));
      setEditingMember(null);
      setEmailStep("idle"); setNewEmail(""); setEmailCode(""); setEmailError("");
    } catch { alert("Failed to save profile"); }
    finally { setMemberLoading(false); }
  }

  async function sendEmailCode() {
    if (!editingMember || !familyId) return;
    setEmailError("");
    setMemberLoading(true);
    try {
      await api.requestEmailChange(familyId, editingMember.memberId, newEmail);
      setEmailStep("verify");
    } catch { setEmailError("Failed to send code — check the email address"); }
    finally { setMemberLoading(false); }
  }

  async function confirmEmailChange() {
    if (!editingMember || !familyId) return;
    setEmailError("");
    setMemberLoading(true);
    try {
      await api.verifyEmailChange(familyId, editingMember.memberId, newEmail, emailCode);
      api.listMembers(familyId).then((m) => setMembers(m as FamilyMember[]));
      setEditingMember(null);
      setEmailStep("idle"); setNewEmail(""); setEmailCode(""); setEmailError("");
    } catch { setEmailError("Invalid or expired code"); }
    finally { setMemberLoading(false); }
  }

  async function toggleBlockMember(memberId: string, blocked: boolean) {
    if (!familyId) return;
    await api.updateMember(familyId, memberId, { blocked });
    api.listMembers(familyId).then((r) => setMembers(r as FamilyMember[]));
  }

  async function removeMember(memberId: string) {
    if (!familyId) return;
    setConfirmRemoveId(memberId);
  }

  async function confirmRemoveMember() {
    if (!familyId || !confirmRemoveId) return;
    const id = confirmRemoveId;
    setConfirmRemoveId(null);
    try {
      await api.deleteMember(familyId, id);
      api.listMembers(familyId).then((m) => setMembers(m as FamilyMember[]));
    } catch { alert("Failed to remove"); }
  }

  async function revokeInvite(inviteCode: string) {
    if (!familyId) return;
    setConfirmRevokeCode(inviteCode);
  }

  async function confirmRevokeInvite() {
    if (!familyId || !confirmRevokeCode) return;
    const code = confirmRevokeCode;
    setConfirmRevokeCode(null);
    try {
      await api.deleteInvite(familyId, code);
      api.listInvites(familyId).then((i) => setInvites(i as FamilyInvite[]));
    } catch { alert("Failed to revoke"); }
  }

  // Kid management handlers
  function refresh() {
    if (!familyId) return;
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
  }

  async function addKid(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !familyId) return;
    setLoading(true);
    await api.createKid(familyId, { name: newName.trim(), avatar: newAva, pin: newPin.trim() || undefined, ...(newDob ? { dob: newDob } : {}) });
    refresh();
    setNewName(""); setNewAva("🧑‍🚀"); setNewPin(""); setNewDob(""); setAdding(false); setLoading(false);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing || !familyId) return;
    setLoading(true);
    await api.updateKid(familyId, editing.kidId, { name: editing.name.trim(), avatar: editing.avatar, ...(editing.dob ? { dob: editing.dob } : {}) });
    refresh();
    setEditing(null); setLoading(false);
  }

  async function toggleBlock(kid: Kid) {
    if (!familyId) return;
    await api.updateKid(familyId, kid.kidId, { blocked: !kid.blocked });
    refresh();
  }

  async function saveResetPin() {
    if (!resetPin || !familyId) return;
    if (resetPin.pin.length < 5 || !/^\d+$/.test(resetPin.pin)) { alert("PIN must be at least 5 digits"); return; }
    setLoading(true);
    try {
      await api.resetKidPin(familyId, resetPin.kidId, resetPin.pin);
      setResetPin(null);
    } catch (e: any) {
      alert("Failed to set PIN: " + (e?.message ?? "unknown error"));
    } finally {
      setLoading(false);
    }
  }

  async function copyFamilyCode() {
    if (!familyCode) return;
    await navigator.clipboard.writeText(familyCode).catch(() => {});
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  }

  async function saveCustomCode() {
    if (!familyId || !codeDraft.trim()) return;
    setCodeError(""); setSavingCode(true);
    try {
      const res = await api.setFamilyCode(familyId, codeDraft.trim());
      setFamilyCode(res.familyCode);
      setEditingCode(false); setCodeDraft("");
    } catch (e: any) {
      setCodeError(e.code === "CODE_TAKEN" ? "That code is already taken — try another" : "Letters and numbers only, no spaces");
    } finally { setSavingCode(false); }
  }

  async function removeKid(kidId: string) {
    if (!familyId) return;
    await api.deleteKid(familyId, kidId);
    refresh();
    setConfirm(null);
  }

  return (
    <div className={styles.page}>
      <ParentSidebar pendingCount={completions.length} />
      <main className={styles.main}>
        <div className={styles.head} style={{ flexDirection: "column", alignItems: "flex-start" }}>
          <h1 className={styles.h1}>👨‍👩‍👧 Family Hub</h1>
          <div className={styles.sub}>Manage your Guardians and {theme.kidRole + "s"}</div>
          {familyCode && !editingCode && (
            <div style={{ marginTop: 14, background: "rgba(255,215,0,0.07)", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 14, padding: "10px 16px", display: "inline-flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,215,0,0.55)" }}>
                Family Code
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 24, letterSpacing: "0.12em", color: "var(--gold)" }}>{familyCode}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={copyFamilyCode} style={{ background: "rgba(255,215,0,0.12)", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 8, color: copiedCode ? "var(--gold)" : "rgba(240,240,255,0.7)", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "var(--font-display)", fontWeight: 700 }}>
                    {copiedCode ? "✓ Copied" : "📋 Copy"}
                  </button>
                  {isAdmin && (
                    <button onClick={() => { setCodeDraft(familyCode); setEditingCode(true); }} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "rgba(240,240,255,0.5)", fontSize: 11, padding: "4px 10px", cursor: "pointer" }}>✏️ Change</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Inline code editor (shown only when editing) */}
        {editingCode && (
          <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
            <input
              style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(255,215,0,0.4)", background: "rgba(0,0,0,0.3)", color: "#fff", fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 18, letterSpacing: "0.15em", textTransform: "uppercase" }}
              placeholder="e.g. SKYWALKERS, GRYFFINDOR, WAKANDA…"
              value={codeDraft}
              onChange={(e) => { setCodeDraft(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")); setCodeError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") saveCustomCode(); if (e.key === "Escape") { setEditingCode(false); setCodeError(""); } }}
              autoFocus
            />
            {codeError && <div style={{ fontSize: 12, color: "var(--coral)" }}>{codeError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveCustomCode} className={`${styles.btn} ${styles.primary}`} disabled={savingCode || codeDraft.length < 2} style={{ fontSize: 12 }}>
                {savingCode ? "Saving…" : "Save Code"}
              </button>
              <button onClick={() => { setEditingCode(false); setCodeError(""); }} className={`${styles.btn} ${styles.ghost}`} style={{ fontSize: 12 }}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Co-parents section ── */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, marginBottom: 12, color: "rgba(240,240,255,0.75)" }}>
            Guardians
          </div>
          <div className={own.grid} style={{ marginBottom: 12 }}>
            {displayMembers.map((m) => {
              const roleLabel = m.role === "admin" ? "Admin" : m.role === "viewer" ? "Viewer" : "Manager";
              const targetIsAdmin = m.role === "admin";
              const canEdit = isAdmin || (m.email === email && !isViewer);
              const canChangeRole = isAdmin && m.email !== email && !targetIsAdmin;
              const canRemove = isAdmin && m.email !== email; // admin can remove anyone except themselves
              const canBlock = isAdmin && m.email !== email && !targetIsAdmin;
              return (
                <div key={m.memberId} className={`${own.card} ${m.blocked ? own.cardBlocked : ""}`}>
                  {m.blocked && <div className={own.blockedTag}>BLOCKED</div>}
                  <div className={own.kidAva}>{m.avatar ?? "👤"}</div>
                  <div className={own.kidName}>{m.displayName ?? m.email}</div>
                  <div className={own.kidLevel}>{roleLabel}</div>
                  {m.displayName && <div style={{ fontSize: 9, color: "rgba(240,240,255,0.35)", fontFamily: "var(--font-mono)" }}>{m.email}</div>}
                  {(canEdit || canRemove || canBlock) && (
                    <>
                      <button className={own.menuBtn} onClick={(e) => openCardMenu(e, `member-${m.memberId}`)}>⋮</button>
                      {openMenu?.id === `member-${m.memberId}` && createPortal(
                        <div ref={menuRef} className={own.menuDropdown}
                          style={{ top: openMenu.y, left: Math.max(8, openMenu.x - 168) }}>
                          {canEdit && <button className={own.menuItem} onClick={() => { setOpenMenu(null); setEditingMember({ memberId: m.memberId, displayName: m.displayName ?? "", avatar: m.avatar ?? "👤", role: m.role === "viewer" ? "viewer" : "manager", canChangeRole, isSelf: m.email === email, currentEmail: m.email, newEmailDirect: "" }); }}>✏️ Edit Profile</button>}
                          {canBlock && <button className={`${own.menuItem} ${m.blocked ? own.menuItemSuccess : own.menuItemWarning}`} onClick={() => { setOpenMenu(null); toggleBlockMember(m.memberId, !m.blocked); }}>{m.blocked ? "✅ Unblock" : "🚫 Block"}</button>}
                          {canRemove && <><div className={own.menuSep} /><button className={`${own.menuItem} ${own.menuItemDanger}`} onClick={() => { setOpenMenu(null); removeMember(m.memberId); }}>🗑 Remove</button></>}
                        </div>, document.body
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Member profile edit form */}
          {editingMember && emailStep === "idle" && (
            <form className={own.form} onSubmit={saveMemberEdit} style={{ marginBottom: 16 }}>
              <div className={own.formTitle}>Edit Profile</div>
              <input className={own.input} placeholder="Display name (optional)" value={editingMember.displayName}
                onChange={(e) => setEditingMember({ ...editingMember, displayName: e.target.value })} autoFocus />
              <div className={own.avatarRow}>
                {PARENT_AVATARS.map((a) => (
                  <button key={a} type="button" className={`${own.ava} ${editingMember.avatar === a ? own.avaActive : ""}`}
                    onClick={() => setEditingMember({ ...editingMember, avatar: a })}>{a}</button>
                ))}
              </div>
              {editingMember.isSelf ? (
                /* Self: OTP verification flow */
                <button type="button" style={{ background: "none", border: "none", color: "rgba(240,240,255,0.5)", fontSize: 11, cursor: "pointer", padding: "6px 0", textAlign: "left", marginBottom: 8 }}
                  onClick={() => { setEmailStep("enter"); setNewEmail(""); }}>
                  ✉️ Change your email address →
                </button>
              ) : (
                /* Admin editing another member: direct change, notification sent to old email */
                <div>
                  <label style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,240,255,0.5)", marginBottom: 4 }}>
                    Email — leave blank to keep current
                  </label>
                  <input className={own.input} type="email" placeholder={editingMember.currentEmail}
                    value={editingMember.newEmailDirect}
                    onChange={(e) => setEditingMember({ ...editingMember, newEmailDirect: e.target.value })} />
                  {editingMember.newEmailDirect.trim() && editingMember.newEmailDirect.trim() !== editingMember.currentEmail && (
                    <div style={{ fontSize: 10, color: "rgba(240,240,255,0.4)", marginTop: 3 }}>
                      ✉️ A notification will be sent to the old email address
                    </div>
                  )}
                </div>
              )}
              {editingMember.canChangeRole && (
                <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <label style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,240,255,0.5)", marginBottom: 6 }}>Role</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    {(["manager", "viewer"] as const).map((r) => (
                      <button key={r} type="button"
                        onClick={() => setEditingMember({ ...editingMember, role: r })}
                        style={{ flex: 1, padding: "8px 10px", borderRadius: 10, cursor: "pointer", textAlign: "center", background: editingMember.role === r ? "rgba(255,215,0,0.1)" : "rgba(255,255,255,0.03)", border: editingMember.role === r ? "1px solid rgba(255,215,0,0.5)" : "1px solid rgba(255,255,255,0.08)", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 12, color: editingMember.role === r ? "var(--gold)" : "rgba(240,240,255,0.6)" }}>
                        {r === "manager" ? "🛠 Manager" : "👁 Viewer"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className={own.formBtns}>
                <button className={`${styles.btn} ${styles.primary}`} disabled={memberLoading}>{memberLoading ? "Saving…" : "Save Profile"}</button>
                <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => setEditingMember(null)}>Cancel</button>
              </div>
            </form>
          )}

          {/* Email change — step 1: enter new email */}
          {editingMember && emailStep === "enter" && (
            <div className={own.form} style={{ marginBottom: 16 }}>
              <div className={own.formTitle}>Change Email</div>
              <input className={own.input} type="email" placeholder="New email address" value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)} autoFocus />
              {emailError && <div style={{ color: "var(--coral)", fontSize: 12 }}>{emailError}</div>}
              <div className={own.formBtns}>
                <button className={`${styles.btn} ${styles.primary}`} onClick={sendEmailCode} disabled={memberLoading || !newEmail.trim()}>
                  {memberLoading ? "Sending…" : "Send Code"}
                </button>
                <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => setEmailStep("idle")}>Back</button>
              </div>
            </div>
          )}

          {/* Email change — step 2: verify OTP */}
          {editingMember && emailStep === "verify" && (
            <div className={own.form} style={{ marginBottom: 16 }}>
              <div className={own.formTitle}>Enter Code</div>
              <div style={{ fontSize: 12, color: "rgba(240,240,255,0.5)", marginBottom: 8 }}>
                A 6-digit code was sent to <strong style={{ color: "var(--gold)" }}>{newEmail}</strong>
              </div>
              <input className={own.input} placeholder="000000" maxLength={6} value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") confirmEmailChange(); }} />
              {emailError && <div style={{ color: "var(--coral)", fontSize: 12 }}>{emailError}</div>}
              <div className={own.formBtns}>
                <button className={`${styles.btn} ${styles.primary}`} onClick={confirmEmailChange} disabled={memberLoading || emailCode.length < 6}>
                  {memberLoading ? "Verifying…" : "Confirm Email"}
                </button>
                <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => setEmailStep("enter")}>Resend</button>
              </div>
            </div>
          )}

          {/* Invite + URL card + pending invites — admin only */}
          {isAdmin && (
            <div>
              {!inviteUrl && !pickingRole && (
                <button className={`${styles.btn} ${styles.primary}`} style={{ marginBottom: 10 }}
                  onClick={() => setPickingRole(true)}>
                  + Invite Guardian
                </button>
              )}

              {!inviteUrl && pickingRole && (
                <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: "16px", marginBottom: 10, maxWidth: 380 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, marginBottom: 12 }}>What role will this Guardian have?</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                    {(["manager", "viewer"] as const).map((r) => (
                      <button key={r} onClick={() => setInviteRole(r)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left", background: inviteRole === r ? "rgba(255,215,0,0.1)" : "rgba(255,255,255,0.03)", border: inviteRole === r ? "1px solid rgba(255,215,0,0.5)" : "1px solid rgba(255,255,255,0.08)" }}>
                        <span style={{ fontSize: 20 }}>{r === "manager" ? "🛠" : "👁"}</span>
                        <span>
                          <span style={{ display: "block", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, color: "#fff" }}>{r === "manager" ? "Manager" : "Viewer"}</span>
                          <span style={{ display: "block", fontSize: 11, color: "rgba(240,240,255,0.45)", marginTop: 1 }}>
                            {r === "manager" ? "Can manage kids and approve tasks" : "Can view everything, no changes"}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className={`${styles.btn} ${styles.primary}`} onClick={() => { generateInvite(); setPickingRole(false); }} disabled={inviting}>
                      {inviting ? "Generating…" : "Generate Invite Link"}
                    </button>
                    <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setPickingRole(false)}>Cancel</button>
                  </div>
                </div>
              )}
              {inviteUrl && (
                <div style={{ background: "rgba(255,215,0,0.06)", border: "1px solid rgba(255,215,0,0.25)", borderRadius: 14, padding: "14px 16px", marginBottom: 10, maxWidth: 520 }}>
                  <div style={{ fontSize: 11, color: "rgba(240,240,255,0.5)", marginBottom: 6 }}>🔗 Share this link — expires in 72 hours</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(240,240,255,0.75)", wordBreak: "break-all", marginBottom: 10 }}>{inviteUrl}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className={`${styles.btn} ${styles.primary}`} onClick={copyInvite}>
                      {copied ? "✓ Copied!" : "📋 Copy Link"}
                    </button>
                    <button className={`${styles.btn} ${styles.ghost}`} onClick={shareInvite}>↗ Share</button>
                    <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setInviteUrl(null)}>✕ Close</button>
                  </div>
                </div>
              )}
              {invites.length > 0 && (
                <div style={{ marginTop: 20, maxWidth: 520 }}>
                  <div style={{ fontSize: 12, color: "rgba(240,240,255,0.5)", marginBottom: 6 }}>
                    Pending invites ({invites.length})
                  </div>
                  {invites.map((inv) => {
                    const expiresAt = new Date(inv.expiresAt * 1000);
                    const hoursLeft = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 3600000));
                    return (
                      <div key={inv.inviteCode} style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 12px", marginBottom: 6, fontSize: 12 }}>
                        <span style={{ color: "rgba(240,240,255,0.5)", flex: 1 }}>
                          ⏳ Expires in {hoursLeft}h · {inv.inviteCode.slice(0, 8)}…
                        </span>
                        {inv.role && (
                          <span style={{ fontSize: 10, fontFamily: "var(--font-display)", fontWeight: 700, color: inv.role === "viewer" ? "rgba(150,200,255,0.8)" : "rgba(255,215,0,0.8)", background: inv.role === "viewer" ? "rgba(100,150,255,0.1)" : "rgba(255,215,0,0.1)", border: `1px solid ${inv.role === "viewer" ? "rgba(100,150,255,0.3)" : "rgba(255,215,0,0.3)"}`, borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>
                            {inv.role === "viewer" ? "Viewer" : "Manager"}
                          </span>
                        )}
                        <button onClick={() => copyInviteLink(inv.inviteCode)} style={{ color: copiedInvite[inv.inviteCode] ? "var(--gold)" : "rgba(240,240,255,0.6)", fontSize: 11, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                          {copiedInvite[inv.inviteCode] ? "✓ Copied" : "📋 Copy Invite"}
                        </button>
                        <button onClick={() => shareInviteLink(inv.inviteCode)} style={{ color: "rgba(240,240,255,0.6)", fontSize: 11, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                          ↗ Share
                        </button>
                        <button onClick={() => revokeInvite(inv.inviteCode)} style={{ color: "var(--coral)", fontSize: 11, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                          Revoke
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Kids section ── */}
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, marginBottom: 12, color: "rgba(240,240,255,0.75)" }}>
          {theme.kidRole + "s"}
        </div>

        {/* Add form */}
        {adding && (
          <form className={own.form} onSubmit={addKid}>
            <div className={own.formTitle}>New {theme.kidRole}</div>
            <input className={own.input} placeholder="Name" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
            <div className={own.avatarRow}>
              {AVATARS.map(a => (
                <button key={a} type="button" className={`${own.ava} ${newAva === a ? own.avaActive : ""}`} onClick={() => setNewAva(a)}>{a}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,240,255,0.5)", marginBottom: 4 }}>
                  Date of Birth
                </label>
                <input className={own.input} type="date" max={new Date().toISOString().split("T")[0]}
                  value={newDob} onChange={e => setNewDob(e.target.value)} />
              </div>
              <div style={{ flex: 2 }}>
                <label style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,240,255,0.5)", marginBottom: 4 }}>
                  Initial PIN (5+ digits)
                </label>
                <input className={own.input} type="password" inputMode="numeric" placeholder="•••••" maxLength={10}
                  value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ""))} />
              </div>
            </div>
            <div className={own.formBtns}>
              <button className={`${styles.btn} ${styles.primary}`} disabled={loading || !newName.trim()}>{loading ? "Adding…" : `Add ${theme.kidRole}`}</button>
              <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => { setAdding(false); setNewName(""); setNewPin(""); setNewDob(""); }}>Cancel</button>
            </div>
          </form>
        )}

        {/* Edit form */}
        {editing && (
          <form className={own.form} onSubmit={saveEdit}>
            <div className={own.formTitle}>Edit {theme.kidRole}</div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 2 }}>
                <label style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,240,255,0.5)", marginBottom: 4 }}>Name</label>
                <input className={own.input} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} autoFocus />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,240,255,0.5)", marginBottom: 4 }}>Date of Birth</label>
                <input className={own.input} type="date" max={new Date().toISOString().split("T")[0]}
                  value={editing.dob} onChange={e => setEditing({ ...editing, dob: e.target.value })} />
              </div>
            </div>
            <div className={own.avatarRow}>
              {AVATARS.map(a => (
                <button key={a} type="button" className={`${own.ava} ${editing.avatar === a ? own.avaActive : ""}`} onClick={() => setEditing({ ...editing, avatar: a })}>{a}</button>
              ))}
            </div>
            <div className={own.formBtns}>
              <button className={`${styles.btn} ${styles.primary}`} disabled={loading || !editing.name.trim()}>{loading ? "Saving…" : "Save Changes"}</button>
              <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        )}

        {confirm && (() => {
          const kid = kids.find((k) => k.kidId === confirm);
          const name = kid?.name || "this explorer";
          return (
            <ConfirmModal
              message={`Remove ${name}?`}
              detail="Their stars, tasks, and history will be permanently deleted."
              confirmLabel="Remove"
              typeToConfirm={name}
              onCancel={() => setConfirm(null)}
              onConfirm={() => removeKid(confirm)}
            />
          );
        })()}

        <div className={own.grid}>
          {kids.length === 0 && !adding && (
            <div className={own.empty}>No explorers yet — add your first!</div>
          )}
          {kids.map((kid) => (
            <div key={kid.kidId} className={`${own.card} ${kid.blocked ? own.cardBlocked : ""}`}>
              {kid.blocked && <div className={own.blockedTag}>BLOCKED</div>}
              <div className={own.kidAva}>{kid.avatar}</div>
              <div className={own.kidName}>{kid.name}</div>
              <div className={own.kidLevel}>Level {kid.level}{kid.dob ? ` · Age ${calcAge(kid.dob)}` : ""}</div>
              {kid.theme && kid.theme !== "base" && (
                <span style={{ fontSize: 14 }}>
                  {{ explorer: "🗺️", wizard: "🧙", sports: "🏆" }[kid.theme] ?? ""}
                </span>
              )}
              {kid.themeRequest && (
                <span style={{ fontSize: 11, color: "rgba(255,215,0,0.7)" }}>⏳</span>
              )}
              <div className={own.kidStats}>
                <div className={own.stat}><span>{theme.pointEmoji}</span>{kid.pointBalance}</div>
                <div className={own.stat}><span>🔥</span>{kid.streakDays}d</div>
                <div className={own.stat}><span>✨</span>{kid.xp} XP</div>
              </div>
              {canManageKids && (
                <>
                  <button className={own.menuBtn} onClick={(e) => openCardMenu(e, `kid-${kid.kidId}`)}>⋮</button>
                  {openMenu?.id === `kid-${kid.kidId}` && createPortal(
                    <div ref={menuRef} className={own.menuDropdown}
                      style={{ top: openMenu.y, left: Math.max(8, openMenu.x - 168) }}>
                      <button className={own.menuItem} onClick={() => { setOpenMenu(null); setEditing({ kidId: kid.kidId, name: kid.name, avatar: kid.avatar, dob: kid.dob ?? "" }); }}>✏️ Edit</button>
                      <button className={own.menuItem} onClick={() => { setOpenMenu(null); setResetPin({ kidId: kid.kidId, pin: "" }); }}>🔑 Reset PIN</button>
                      <button className={`${own.menuItem} ${kid.blocked ? own.menuItemSuccess : own.menuItemWarning}`} onClick={() => { setOpenMenu(null); toggleBlock(kid); }}>{kid.blocked ? "✅ Unblock" : "🚫 Block"}</button>
                      {isAdmin && <><div className={own.menuSep} /><button className={`${own.menuItem} ${own.menuItemDanger}`} onClick={() => { setOpenMenu(null); setConfirm(kid.kidId); }}>🗑 Remove</button></>}
                    </div>, document.body
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {!adding && !editing && canManageKids && (
          <button className={`${styles.btn} ${styles.primary}`} style={{ marginTop: 14 }} onClick={() => setAdding(true)}>
            {`+ Add ${theme.kidRole}`}
          </button>
        )}

        {/* PIN reset form */}
        {resetPin && (
          <div className={own.form} style={{ marginTop: 16, maxWidth: 360 }}>
            <div className={own.formTitle}>🔑 Reset PIN</div>
            <div style={{ fontSize: 12, color: "rgba(240,240,255,0.5)" }}>Set a new PIN for this {theme.kidRole}</div>
            <input className={own.input} type="password" inputMode="numeric" placeholder="New PIN (5+ digits)" maxLength={10}
              value={resetPin.pin} onChange={(e) => setResetPin({ ...resetPin, pin: e.target.value.replace(/\D/g, "") })} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") saveResetPin(); }} />
            <div className={own.formBtns}>
              <button className={`${styles.btn} ${styles.primary}`} onClick={saveResetPin} disabled={loading || resetPin.pin.length < 5}>
                {loading ? "Saving…" : "Set PIN"}
              </button>
              <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => setResetPin(null)}>Cancel</button>
            </div>
          </div>
        )}

      </main>
      <BottomNav />
      {confirmRevokeCode && (
        <ConfirmModal
          message="Revoke this invite link?"
          detail="Anyone with this link will no longer be able to join your family."
          confirmLabel="Revoke"
          onCancel={() => setConfirmRevokeCode(null)}
          onConfirm={confirmRevokeInvite}
        />
      )}

      {confirmRemoveId && (() => {
        const m = members.find((x) => x.memberId === confirmRemoveId);
        const label = m?.displayName || m?.email || "guardian";
        return (
          <ConfirmModal
            message={`Remove ${label}?`}
            detail="They will lose access to your family. This cannot be undone."
            confirmLabel="Remove"
            typeToConfirm={label}
            onCancel={() => setConfirmRemoveId(null)}
            onConfirm={confirmRemoveMember}
          />
        );
      })()}
    </div>
  );
}
